import { Component, inject, OnDestroy, OnInit, output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Timestamp } from 'firebase/firestore';
import { Vital } from 'src/app/calvin-emr-shared/models/vitals.model';

import { SelectItem } from 'primeng/api';
import { InputNumberModule } from 'primeng/inputnumber';
import { Select } from 'primeng/select';

import { ButtonDirective } from 'primeng/button';
import { FloatLabel } from 'primeng/floatlabel';
import { Subscription } from 'rxjs';

type FlushReason = 'manual' | 'tab-switch' | 'route-leave' | 'destroy';
@Component({
  selector: 'app-vitals-form',
  templateUrl: './vitals-form.component.html',
  styleUrls: ['./vitals-form.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    InputNumberModule,
    Select,
    ButtonDirective,
    FloatLabel
],
})
export class VitalsFormComponent implements OnInit, OnDestroy {
  private fb = inject(UntypedFormBuilder);
  vitalsForm = this.fb.group({
    heartRate: [],
    oxySat: [],
    oxygen: [],
    oxygenInput: [],
    respirations: [],
    temperature: [],
    systolic: [],
    diastolic: [],
  });

  newVitalEvent = output<Vital>();

  // Dropdown options for oxygen levels
  oxygenOptions: SelectItem[] = [
    { label: 'Nasal Cannula', value: 'Nasal Cannula' },
    { label: 'Venturi Mask', value: 'Venturi Mask' },
    { label: 'Non-Rebreather', value: 'Non-Rebreather' },
  ];

  private oxygenSub?: Subscription;
  private hasManualSubmit = false;
  private lastSavedFingerprint: string | null = null;

  ngOnInit(): void {
    this.oxygenSub = this.vitalsForm.get('oxygen')?.valueChanges.subscribe((value) => {
      if (value === 'Nasal Cannula' || value === 'Venturi Mask') {
        this.vitalsForm.get('oxygenInput')?.setValidators(Validators.required);
      } else {
        this.vitalsForm.get('oxygenInput')?.clearValidators();
        this.vitalsForm.get('oxygenInput')?.setValue('');
      }
      this.vitalsForm.get('oxygenInput')?.updateValueAndValidity();
    });
  }

  ngOnDestroy(): void {
    this.oxygenSub?.unsubscribe();
    this.flushPendingVital('destroy');
  }

  addNewVital(): void {
    const saved = this.flushPendingVital('manual');
    if (saved) {
      this.hasManualSubmit = true;
      this.vitalsForm.reset();
      this.lastSavedFingerprint = null;
      this.hasManualSubmit = false;
    }
  }

  flushPendingVital(reason: FlushReason): boolean {
    if (!this.canFlush(reason)) return false;

    const newVital = this.buildVitalFromForm();
    this.newVitalEvent.emit(newVital);
    this.lastSavedFingerprint = this.getFingerprint();

    this.vitalsForm.reset();

    return true;
  }

  private canFlush(reason: FlushReason): boolean {
    if (!this.vitalsForm.dirty) return false;
    if (!this.hasAnyValue()) return false;

    // Keep manual submit as the explicit source-of-truth action.
    if (reason !== 'manual' && this.hasManualSubmit) return false;

    const currentFingerprint = this.getFingerprint();
    if (this.lastSavedFingerprint === currentFingerprint) return false;

    // Honor existing form validity for required conditional fields.
    if (!this.vitalsForm.valid) return false;

    return true;
  }

  private hasAnyValue(): boolean {
    const v = this.vitalsForm.value;
    return [
      v.heartRate,
      v.oxySat,
      v.oxygen,
      v.oxygenInput,
      v.respirations,
      v.temperature,
      v.systolic,
      v.diastolic,
    ].some((x) => x !== null && x !== undefined && x !== '');
  }

  private getFingerprint(): string {
    const v = this.vitalsForm.value;
    return JSON.stringify({
      heartRate: v.heartRate ?? '',
      oxySat: v.oxySat ?? '',
      oxygen: v.oxygen ?? '',
      oxygenInput: v.oxygenInput ?? '',
      respirations: v.respirations ?? '',
      temperature: v.temperature ?? '',
      systolic: v.systolic ?? '',
      diastolic: v.diastolic ?? '',
    });
  }

  private buildVitalFromForm(): Vital {
    return new Vital(
      Timestamp.now(),
      this.vitalsForm.value.heartRate,
      this.vitalsForm.value.oxySat,
      this.vitalsForm.value.oxygen,
      this.vitalsForm.value.oxygenInput,
      this.vitalsForm.value.respirations,
      this.vitalsForm.value.temperature,
      this.vitalsForm.value.systolic,
      this.vitalsForm.value.diastolic,
      false
    );
  }



  get heartRateFormControl(): UntypedFormControl {
    return this.vitalsForm.controls.heartRate as UntypedFormControl;
  }

  get oxySatFormControl(): UntypedFormControl {
    return this.vitalsForm.controls.oxySat as UntypedFormControl;
  }

  get oxygenFormControl(): UntypedFormControl {
    return this.vitalsForm.controls.oxygen as UntypedFormControl;
  }

  get oxygenInputFormControl(): UntypedFormControl {
    return this.vitalsForm.controls.oxygenInput as UntypedFormControl;
  }

  get respirationsFormControl(): UntypedFormControl {
    return this.vitalsForm.controls.respirations as UntypedFormControl;
  }

  get temperatureFormControl(): UntypedFormControl {
    return this.vitalsForm.controls.temperature as UntypedFormControl;
  }

  get systolicFormControl(): UntypedFormControl {
    return this.vitalsForm.controls.systolic as UntypedFormControl;
  }

  get diastolicFormControl(): UntypedFormControl {
    return this.vitalsForm.controls.diastolic as UntypedFormControl;
  }
}
